import React, { useCallback, useRef } from 'react';
import { Block, BulletListBlockData, listMarker, listStyleOf } from '../../../config';
import LiveText from '../../LiveText';
import { readSubscripts } from '../../../subscript';
import './bullet-list-block.scss';

interface BulletListBlockProps {
  block: Block;
  onChange: (data: BulletListBlockData) => void;
  onDelete: () => void;
  isEditing: boolean;
  /**
   * Leave the list and carry on in ordinary text: the items to keep, and then
   * a paragraph below them. The canvas owns both halves of that so the list
   * cannot save its items and have the new paragraph overwrite them.
   */
  onExitList?: (remainingItems: string[]) => void;
}

const BulletListBlock: React.FC<BulletListBlockProps> = ({ block, onChange, isEditing, onExitList }) => {
  const data = block.data as BulletListBlockData;
  const listRef = useRef<HTMLUListElement | HTMLOListElement>(null);

  const updateItem = useCallback((index: number, text: string) => {
    const newItems = [...data.items];
    newItems[index] = text;
    onChange({ ...data, items: newItems });
  }, [data, onChange]);

  /** Move the caret to the end of an item, by position. */
  const focusItem = useCallback((index: number) => {
    setTimeout(() => {
      const items = listRef.current?.querySelectorAll('.item-text');
      const item = items?.[index] as HTMLElement | undefined;
      if (!item) return;
      item.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(item);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }, 0);
  }, []);

  const deleteItem = useCallback((index: number) => {
    const newItems = data.items.filter((_, i) => i !== index);
    onChange({ ...data, items: newItems });
    focusItem(Math.max(0, index - 1));
  }, [data, onChange, focusItem]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    // As LiveText reads it: a subscript stays one (subscript.ts).
    const currentText = readSubscripts(e.target as HTMLElement);
    const isLast = index === data.items.length - 1;

    if (e.key === 'Enter') {
      e.preventDefault();

      // Enter on the trailing empty bullet ends the list rather than adding
      // another one -- the word processor behaviour, and the only way out of a
      // list without reaching for the mouse.
      if (isLast && !currentText.trim() && onExitList) {
        onExitList(data.items.slice(0, index));
        return;
      }

      // Save the current text AND insert the new item in one update. Two separate
      // onChange calls would both be built from the same stale `data.items`, and
      // the second would overwrite the first (losing the typed text).
      const newItems = [...data.items];
      newItems[index] = currentText;
      newItems.splice(index + 1, 0, '');
      onChange({ ...data, items: newItems });

      // Focus the new item after React re-renders
      setTimeout(() => {
        const items = listRef.current?.querySelectorAll('.item-text');
        const newItem = items?.[index + 1] as HTMLElement;
        newItem?.focus();
      }, 0);
    } else if (e.key === 'Backspace') {
      if (currentText.trim()) return;
      e.preventDefault();

      // Backspacing away the last bullet leaves the list entirely, so an empty
      // list can never be stranded on the page with nothing to type into.
      if (data.items.length <= 1) {
        onExitList?.([]);
        return;
      }
      deleteItem(index);
    }
  }, [data, onChange, deleteItem, onExitList]);

  // Bullets are the browser's disc, which DocumentPDF's listBullet was tuned
  // against. Numbers and letters are drawn here from listMarker, the text the
  // PDF prints, in the same 12.7mm gutter with the same gap: Chrome's own
  // decimal / lower-alpha marker is "1. " with a trailing space, a narrower
  // gap than the PDF's 7px, which put the PDF's markers ~2.5pt further left.
  const style = listStyleOf(data);
  const ListTag = style === 'bullet' ? 'ul' : 'ol';

  return (
    <div className="bullet-list-block">
      <ListTag
        ref={listRef as React.RefObject<HTMLUListElement> & React.RefObject<HTMLOListElement>}
        className={`bullet-list bullet-list--${style}`}
      >
        {data.items.map((item, index) => (
          <li key={index} className="list-item" data-marker={style === 'bullet' ? undefined : listMarker(style, index)}>
            <LiveText
              className="item-text"
              value={item}
              editable={isEditing}
              restoreKey={`${block.id}:${index}`}
              onCommit={(text) => updateItem(index, text)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-placeholder="Enter item..."
            />
          </li>
        ))}
      </ListTag>
    </div>
  );
};

export default BulletListBlock;
